import type { components } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { trackTeamCreate } from '../analytics/team';
import { createTeam } from '../api/teams';
import { isInviteModalV2OpenAtom } from '../atoms/teamAtoms';
import { GET_JOINED_TEAMS } from '../query/useQueryJoinedTeams';

type ErrorResponse = components['schemas']['ErrorResponse'];

type UseMutateCreateTeamOptions = {
  billingThirdPartyLicense?: components['schemas']['ThirdPartyLicenceDetails'] | null;
  onCreateTeamError?: (detailMsg: string) => void;
};

export function useMutateCreateTeam({
  billingThirdPartyLicense,
  onCreateTeamError,
}: UseMutateCreateTeamOptions = {}) {
  const queryClient = useQueryClient();

  const setInviteModalV2Open = useSetAtom(isInviteModalV2OpenAtom);

  return useMutation({
    mutationFn: async (variables: { teamName: string }) => {
      const data = await createTeam(variables.teamName);

      return data;
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_JOINED_TEAMS],
      });
      // tracking
      trackTeamCreate({
        teamName: variables.teamName,
        teamId: data.data.team.id,
      });

      // If user's account is managed by reseller license, don't allow inviting new members
      if (!billingThirdPartyLicense) {
        setInviteModalV2Open(true);
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse;
        onCreateTeamError?.(errorData.detail.msg);
      }
    },
  });
}
